<script lang="ts" setup>
import * as THREE from 'three'
import { OrbitControls } from 'three/examples/jsm/controls/OrbitControls'
import { onBeforeUnmount, onMounted, ref } from 'vue'

const props = defineProps<{
  inView: boolean
}>()

const sceneContainer = ref<HTMLDivElement | null>(null)
let scene: THREE.Scene, camera: THREE.PerspectiveCamera, renderer: THREE.WebGLRenderer
let cube: THREE.Mesh, controls: OrbitControls
let axesHelper: THREE.AxesHelper, plane: THREE.Mesh
const animationId = ref<number | null>(null)

// Function to initialize Three.js scene
function initScene() {
  // Create scene
  scene = new THREE.Scene()
  scene.background = new THREE.Color(0x0A0A0A)

  // Setup camera
  const aspectRatio = sceneContainer.value!.clientWidth / sceneContainer.value!.clientHeight
  camera = new THREE.PerspectiveCamera(75, aspectRatio, 0.1, 1000)
  camera.position.set(3, 3, 5)

  // Setup renderer
  renderer = new THREE.WebGLRenderer({ antialias: true })
  renderer.setSize(sceneContainer.value!.clientWidth, sceneContainer.value!.clientHeight)
  sceneContainer.value!.appendChild(renderer.domElement)

  // Create a cube
  const geometry = new THREE.BoxGeometry()
  const material = new THREE.MeshStandardMaterial({ color: 0x0077FF })
  cube = new THREE.Mesh(geometry, material)
  cube.translateY(0.55)
  scene.add(cube)

  // Create a plane
  const planeGeometry = new THREE.PlaneGeometry(10, 10)
  const planeMaterial = new THREE.MeshStandardMaterial({ color: 0xAAAAAA, side: THREE.DoubleSide })
  plane = new THREE.Mesh(planeGeometry, planeMaterial)
  plane.rotation.x = -Math.PI / 2 // Rotate plane to be horizontal
  scene.add(plane)

  // Add lights
  const ambientLight = new THREE.AmbientLight(0xFFFFFF, 0.5)
  scene.add(ambientLight)

  const directionalLight = new THREE.DirectionalLight(0xFFFFFF, 1)
  directionalLight.position.set(5, 5, 5)
  scene.add(directionalLight)

  // Create OrbitControls for navigation
  controls = new OrbitControls(camera, renderer.domElement)
  controls.enableDamping = true // Smooth controls

  // Add axes helper for x, y, z axis indicators
  axesHelper = new THREE.AxesHelper(5)
  scene.add(axesHelper)
}

// Function to animate the scene
function animate() {
  animationId.value = requestAnimationFrame(animate)

  // Update cube rotation
  cube.rotation.y += 0.01

  // Update controls
  controls.update()

  // Render the scene
  renderer.render(scene, camera)
}

// Resize handler
function handleResize() {
  if (sceneContainer.value) {
    camera.aspect = sceneContainer.value.clientWidth / sceneContainer.value.clientHeight
    camera.updateProjectionMatrix()
    renderer.setSize(sceneContainer.value.clientWidth, sceneContainer.value.clientHeight)
  }
}

onMounted(() => {
  initScene()
  animate()

  window.addEventListener('resize', handleResize)
})

onBeforeUnmount(() => {
  if (animationId.value)
    cancelAnimationFrame(animationId.value)
  window.removeEventListener('resize', handleResize)

  // Clean up Three.js resources
  controls.dispose()
  renderer.dispose()
})
</script>

<template>
  <div ref="sceneContainer" class="scene-container w-full h-full" />
</template>
